import { RequestValues } from "../../../utils/request-client.js";
import {
  adaptAnalysisToProblems,
  adaptHistory,
} from "../adapters/deepAnalysisAdapter.js";

/**
 * All three Deep Analysis calls live here.
 *
 *   history  -> bootstrap()       runs first, on mount
 *   status   -> pollOnce()        after bootstrap finds a pending run
 *   trigger  -> runNewAnalysis()  only on a button click, it bills ~$1
 *
 * A singleton because a run takes minutes and must outlive the page.
 * Same shape as the existing LLMModal/services/chatService.js.
 */

// --- status vocabulary ----------------------------------------------------

/**
 * The spec only names "pending", never the success value.
 * So: match pending and failed, treat anything else as success.
 */
const PENDING_STATUSES = new Set([
  "pending",
  "running",
  "queued",
  "processing",
  "in_progress",
  "in-progress",
  "started",
  "created",
]);

const FAILED_STATUSES = new Set([
  "failed",
  "error",
  "cancelled",
  "canceled",
  "timeout",
]);

const isPending = (s) => PENDING_STATUSES.has(String(s || "").toLowerCase());
const isFailed = (s) => FAILED_STATUSES.has(String(s || "").toLowerCase());
const isTerminalSuccess = (s) => Boolean(s) && !isPending(s) && !isFailed(s);

// --- polling policy -------------------------------------------------------

/** 10s minimum, 30s maximum. Last value repeats. */
const BACKOFF_MS = [10000, 10000, 15000, 15000, 20000, 20000, 30000];

/** Wall-clock ceiling. On expiry, offer Refresh — never auto-retrigger. */
const MAX_POLL_MS = 15 * 60 * 1000;

/** Transient network blips shouldn't kill a 15-minute poll. */
const MAX_CONSECUTIVE_ERRORS = 3;

/** Grace period before teardown, so a quick trip off the page is free. */
const IDLE_TEARDOWN_MS = 60 * 1000;

// --- pending marker -------------------------------------------------------

/**
 * A run in flight, remembered across a page reload.
 *
 * The global notifier (mounted beside every page) only wakes this store when
 * this key is set. Without it, every page in the app would pay for two Deep
 * Analysis calls it has no use for.
 */
const PENDING_KEY = "money.deepAnalysis.pending";

/** Longer than MAX_POLL_MS, so a run we gave up on still gets one last look. */
const PENDING_TTL_MS = 60 * 60 * 1000;

function markPending(analysisId) {
  try {
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ id: analysisId, at: Date.now() })
    );
  } catch {
    // storage full or blocked — the in-page poll still works
  }
}

function clearPending() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    // nothing to do
  }
}

/** True when a run was started here and hasn't been seen through to an end. */
export function hasPendingRun() {
  try {
    const raw = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
    if (!raw || !raw.id) return false;
    if (Date.now() - (raw.at || 0) > PENDING_TTL_MS) {
      clearPending(); // too old to still be running
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

// --- state ----------------------------------------------------------------

const INITIAL_STATE = {
  // idle | bootstrapping | pending | ready | failed | timeout | error
  phase: "idle",
  analysisId: null,
  problems: [],
  moduleErrors: [],
  /** module -> status, straight from the API. Drives the button label. */
  moduleStatus: {},
  modules: [],
  history: [],
  totals: {},
  costUsd: 0,
  startedAt: null,
  pollAttempt: 0,
  error: null,
  /**
   * Set once, when a run WE watched reaches an end. Drives the app-wide
   * banner. `at` is the read-once token — the notifier fires on a new one.
   * { kind: "ready" | "failed", analysisId, totalSavings, at }
   */
  notice: null,
};

let state = INITIAL_STATE;
let listeners = new Set();

// Polling internals (deliberately outside React)
let pollTimer = null;
let teardownTimer = null;
let controller = null;
let inFlight = false;
let pollDeadline = 0;
let consecutiveErrors = 0;
let bootstrapPromise = null;

/** Replace state wholesale — useSyncExternalStore needs a stable reference. */
function setState(patch) {
  const next = { ...state, ...patch };
  state = next;
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.error("[deepAnalysisStore] listener failed:", err);
    }
  });
}

export function getSnapshot() {
  return state;
}

export function subscribe(listener) {
  listeners.add(listener);
  // Coming back cancels a pending teardown.
  if (teardownTimer) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }
  // Stay away longer than the grace period and polling was torn down.
  // ensureLoaded won't restart it (phase is "pending", not "idle"), so the
  // bar would sit on "Analysing…" forever. Pick it back up here.
  if (state.phase === "pending" && !pollTimer && !inFlight) pollOnce();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) scheduleTeardown();
  };
}

function scheduleTeardown() {
  if (teardownTimer) clearTimeout(teardownTimer);
  teardownTimer = setTimeout(() => {
    if (listeners.size === 0) stopPolling();
    teardownTimer = null;
  }, IDLE_TEARDOWN_MS);
}

// --- helpers --------------------------------------------------------------

/**
 * Email is a path segment of every Deep Analysis call.
 * Only one of the four login paths writes localStorage.email, so fall
 * back to user_info — otherwise a valid session looks signed out.
 */
function getEmail() {
  const direct = localStorage.getItem("email");
  if (direct) return direct;

  try {
    const info = JSON.parse(localStorage.getItem("user_info") || "null");
    if (info && info.email) return info.email;
  } catch {
    // malformed user_info — fall through
  }
  return "";
}

function stopPolling() {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  if (controller) {
    controller.abort();
    controller = null;
  }
  inFlight = false;
}

function backoffFor(attempt) {
  return BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
}

/** Per-module progress, if the response carries it. */
function progressFrom(data) {
  const ms = data && data.module_status;
  return {
    moduleStatus: ms && typeof ms === "object" && !Array.isArray(ms) ? ms : {},
    modules: Array.isArray(data && data.modules) ? data.modules : [],
  };
}

/**
 * Applies a completed analysis payload to state.
 *
 * `watched` is true only when this finishes a run we were polling — that is
 * the one case where the user is owed a "your analysis is ready" banner.
 * Opening an old run from the history dropdown must not raise one.
 */
function applyResult(analysis, analysisId, watched = false) {
  const { problems, moduleErrors, totals } = adaptAnalysisToProblems(analysis);
  const id = analysisId || state.analysisId;
  if (watched) clearPending();

  setState({
    phase: "ready",
    analysisId: id,
    problems,
    moduleErrors,
    totals,
    costUsd: totals.costUsd || 0,
    ...progressFrom(analysis),
    error: null,
    notice: watched
      ? {
          kind: "ready",
          analysisId: id,
          totalSavings: totals.totalSavings || 0,
          at: Date.now(),
        }
      : state.notice,
  });
}

/** Ends a watched run badly — same banner path, so nobody waits forever. */
function failWatched(message, analysisId) {
  clearPending();
  setState({
    phase: "failed",
    error: message,
    notice: {
      kind: "failed",
      analysisId: analysisId || state.analysisId,
      totalSavings: 0,
      at: Date.now(),
    },
  });
}

/** The notifier calls this once it has shown the banner. */
export function clearNotice() {
  if (state.notice) setState({ notice: null });
}

// --- 2. status endpoint (polling) -----------------------------------------

async function pollOnce() {
  // setTimeout recursion, not setInterval, so polls can never overlap.
  if (inFlight) return;

  const email = getEmail();
  const analysisId = state.analysisId;
  if (!email || !analysisId) return;

  // Deadline reached — stop, but leave the run recoverable via Refresh.
  if (Date.now() > pollDeadline) {
    stopPolling();
    setState({ phase: "timeout" });
    return;
  }

  inFlight = true;
  const myController = new AbortController();
  controller = myController;
  const signal = myController.signal;

  try {
    const data = await RequestValues("deepAnalysisStatusGet", {
      pathParams: { email, analysisId },
      signal,
    });

    consecutiveErrors = 0;
    const status = data?.status;

    if (isPending(status)) {
      setState({
        phase: "pending",
        pollAttempt: state.pollAttempt + 1,
        ...progressFrom(data),
      });
      scheduleNextPoll();
    } else if (isFailed(status)) {
      stopPolling();
      failWatched(
        data?.error || data?.message || "The analysis failed.",
        analysisId
      );
    } else if (isTerminalSuccess(status)) {
      stopPolling();
      applyResult(data, analysisId, true);
    } else {
      // No usable status field at all — keep polling rather than guessing.
      setState({ pollAttempt: state.pollAttempt + 1, ...progressFrom(data) });
      scheduleNextPoll();
    }
  } catch (err) {
    // Abort is normal teardown. Check the signal; error.name is unreliable.
    if (signal.aborted || err?.isAbort) return;

    // 404 means the id is gone; retrying cannot help.
    if (err?.status === 404) {
      stopPolling();
      failWatched(
        "This analysis could not be found. Try running a new one.",
        analysisId
      );
      return;
    }

    consecutiveErrors += 1;
    if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
      stopPolling();
      setState({
        phase: "error",
        error: err?.message || "Lost connection while checking the analysis.",
      });
      return;
    }
    scheduleNextPoll(); // ride out transient blips
  } finally {
    inFlight = false;
    // Only clear if still the active controller.
    if (controller === myController) controller = null;
  }
}

function scheduleNextPoll() {
  if (pollTimer) clearTimeout(pollTimer);

  // Nothing runs in a hidden tab; visibilitychange resumes it.
  if (typeof document !== "undefined" && document.hidden) return;

  pollTimer = setTimeout(pollOnce, backoffFor(state.pollAttempt));
}

function startPolling(analysisId) {
  stopPolling();
  pollDeadline = Date.now() + MAX_POLL_MS;
  consecutiveErrors = 0;
  markPending(analysisId);
  setState({
    phase: "pending",
    analysisId,
    pollAttempt: 0,
    startedAt: Date.now(),
    // Clear the previous run, or the button shows its finished modules.
    moduleStatus: {},
    modules: [],
    error: null,
    // The last run's banner must not resurface behind this one.
    notice: null,
  });
  // First check immediately — a fast analysis may already be done.
  pollOnce();
}

// --- 3. history endpoint (bootstrap) --------------------------------------

/** Idempotent. Safe to call on every mount. */
export function ensureLoaded() {
  if (bootstrapPromise) return bootstrapPromise;
  if (state.phase !== "idle") return Promise.resolve();

  bootstrapPromise = bootstrap().finally(() => {
    bootstrapPromise = null;
  });
  return bootstrapPromise;
}

async function bootstrap() {
  const email = getEmail();
  if (!email) {
    setState({
      phase: "error",
      // Both email sources absent. The session is usually still fine.
      error:
        "Couldn't identify your account for this analysis. Sign out and sign back in, then try again.",
    });
    return;
  }

  setState({ phase: "bootstrapping", error: null });

  try {
    const data = await RequestValues("deepAnalysisHistoryGet", {
      pathParams: { email },
    });

    // The list may be bare or wrapped — accept both.
    const rawList = Array.isArray(data)
      ? data
      : data?.analyses || data?.data || data?.items || [];
    const history = adaptHistory(rawList);

    setState({ history });

    const latest = history[0];
    if (!latest) {
      // Nothing to reuse. Show the CTA — never auto-trigger, it costs money.
      setState({ phase: "idle" });
      return;
    }

    if (isPending(latest.status)) {
      // Resume the run already in flight. This is what makes a mid-analysis
      // page refresh seamless instead of starting (and billing) a second run.
      startPolling(latest.id);
      return;
    }

    if (isFailed(latest.status)) {
      // Already over before this page loaded — show it, don't announce it.
      clearPending();
      setState({
        phase: "failed",
        analysisId: latest.id,
        error: "The last analysis failed.",
      });
      return;
    }

    // Completed — fetch the full body, since history rows carry only metadata.
    await loadAnalysis(latest.id);
  } catch (err) {
    setState({
      phase: "error",
      error: err?.message || "Couldn't load previous analyses.",
    });
  }
}

/** Fetches one analysis in full and renders it. */
async function loadAnalysis(analysisId) {
  const email = getEmail();
  if (!email || !analysisId) return;

  try {
    const data = await RequestValues("deepAnalysisStatusGet", {
      pathParams: { email, analysisId },
    });

    if (isPending(data?.status)) {
      startPolling(analysisId);
      return;
    }
    if (isFailed(data?.status)) {
      clearPending();
      setState({
        phase: "failed",
        analysisId,
        error: data?.error || "This analysis failed.",
      });
      return;
    }
    applyResult(data, analysisId);
  } catch (err) {
    setState({
      phase: "error",
      analysisId,
      error: err?.message || "Couldn't load that analysis.",
    });
  }
}

// --- 1. trigger endpoint --------------------------------------------------

/**
 * Starts a NEW analysis. Only ever call this from an explicit user action —
 * each run bills real money.
 */
export async function runNewAnalysis() {
  const email = getEmail();
  if (!email) {
    setState({
      phase: "error",
      // Only reached when BOTH "email" and user_info.email are absent —
      // see getEmail(). Says what is missing instead of implying the
      // session is dead, because it usually is not.
      error:
        "Couldn't identify your account for this analysis. Sign out and sign back in, then try again.",
    });
    return;
  }
  // Don't stack runs.
  if (state.phase === "pending" || state.phase === "bootstrapping") return;

  stopPolling();
  setState({ phase: "bootstrapping", error: null, problems: [] });

  try {
    const data = await RequestValues("deepAnalysisTrigger", {
      pathParams: { email },
      // email is already in the path; the documented spec also sends it in the
      // body, so we match the spec exactly rather than second-guessing it.
      body: { email },
    });

    const analysisId =
      data?.analysis_id || data?.analysisId || data?.id || null;

    if (!analysisId) {
      setState({
        phase: "error",
        error: "The server didn't return an analysis id.",
      });
      return;
    }
    startPolling(analysisId);
  } catch (err) {
    setState({
      phase: "error",
      error: err?.message || "Couldn't start the analysis.",
    });
  }
}

/** Switches the view to a previous run from the history dropdown. */
export async function selectFromHistory(analysisId) {
  if (!analysisId || analysisId === state.analysisId) return;
  stopPolling();
  setState({ phase: "bootstrapping", analysisId, error: null });
  await loadAnalysis(analysisId);
}

/** Manual retry — used by the timeout and error states. */
export async function refresh() {
  if (state.analysisId) {
    stopPolling();
    pollDeadline = Date.now() + MAX_POLL_MS;
    consecutiveErrors = 0;
    setState({ phase: "pending", pollAttempt: 0, error: null });
    await pollOnce();
    return;
  }
  state = INITIAL_STATE;
  await ensureLoaded();
}

/** Test/debug helper — resets everything. */
export function __reset() {
  stopPolling();
  clearPending();
  if (teardownTimer) clearTimeout(teardownTimer);
  teardownTimer = null;
  bootstrapPromise = null;
  state = INITIAL_STATE;
  listeners.forEach((fn) => fn());
}

// --- visibility -----------------------------------------------------------

// Poll immediately when the user comes back to the tab, and stop scheduling
// while it's hidden (scheduleNextPoll bails on document.hidden).
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && state.phase === "pending" && !inFlight) {
      pollOnce();
    }
  });
}
